"use client";

import { useState, useEffect, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import {
  MonitorUp,
  Workflow,
  Landmark,
  FileJson,
  CheckCircle2,
  Sparkles,
  ChevronRight,
  Activity
} from "lucide-react";

const getNhcxHeroTranslations = (locale: string) => {
  if (locale === "hi") {
    return {
      liveRouter: "लाइव एक्सचेंज राउटर",
      bundleSent: "बंडल भेजा गया",
      claimRouted: "दावा अग्रेषित",
      approvedAmt: "स्वीकृत ₹84,000",
      waitingPayload: "पेलोड ट्रिगर की प्रतीक्षा में...",
      hospital: "Chikitsa EMR",
      gateway: "NHCX गेटवे",
      payer: "बीमाकर्ता (Payer)",
      log1: "नैदानिक ​​दस्तावेज संकलित किए जा रहे हैं (102)...",
      log2: "NHCX के लिए FHIR पेलोड शुरू किया जा रहा है...",
      log3: "गेटवे पर पैकेट प्राप्त हुआ।",
      log4: "ClaimIQ: 99% पास · जोखिम स्कोर: 4%",
      log5: "बीमाकर्ता को सुरक्षित रूप से अग्रेषित किया जा रहा है...",
      log6: "बीमाकर्ता पर पैकेट प्राप्त हुआ।",
      log7: "दावा नियमों को ऑटो-निर्णय दिया जा रहा है...",
      log8: "प्रतिक्रिया: स्वीकृत (₹84,000)",
      log9: "भुगतान पुष्टि वापस भेजी जा रही है...",
      log10: "अस्पताल एस्क्रो खाता अपडेट किया गया।",
      log11: "मरीज डिस्चार्ज के लिए तैयार।",
    };
  }
  return {
    liveRouter: "Live Exchange Router",
    bundleSent: "BUNDLE SENT",
    claimRouted: "CLAIM ROUTED",
    approvedAmt: "APPROVED ₹84,000",
    waitingPayload: "Waiting for payload trigger...",
    hospital: "Chikitsa EMR",
    gateway: "NHCX Gateway",
    payer: "Payer",
    log1: "Compiling clinical documents (102)...",
    log2: "Initiating FHIR payload to NHCX...",
    log3: "Packet received at Gateway.",
    log4: "ClaimIQ: 99% PASS · Risk Score: 4%",
    log5: "Forwarding securely to Payer...",
    log6: "Packet received at Payer.",
    log7: "Auto-adjudicating claim rules...",
    log8: "Response: SANCTIONED (₹84,000)",
    log9: "Routing payment confirmation back...",
    log10: "Hospital escrow ledger updated.",
    log11: "Ready for patient discharge.",
  };
};

export function NhcxHeroAnimation({ locale = "en" }: { locale?: string }) {
  const t = getNhcxHeroTranslations(locale);
  const [mounted, setMounted] = useState(false);
  const [phase, setPhase] = useState<
    "idle" | "sending_to_gw" | "scanning" | "sending_to_tpa" | "adjudicating" | "returning" | "approved"
  >("idle");
  const [logs, setLogs] = useState<{ time: string; msg: string; type: "info" | "success" | "warn" }[]>([]);
  const cycleStartRef = useRef<number>(Date.now());
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  const addLog = (msg: string, type: "info" | "success" | "warn" = "info") => {
    const ms = String(Date.now() - cycleStartRef.current).padStart(4, "0");
    setLogs((prev) => [...prev, { time: ms, msg, type }].slice(-5));
  };

  useEffect(() => {
    setMounted(true);
  }, []);

  const tRef = useRef(t);
  tRef.current = t;

  useEffect(() => {
    if (!mounted) return;

    const runCycle = () => {
      const t = tRef.current;
      setPhase("idle");
      setLogs([]);
      cycleStartRef.current = Date.now();
      
      timerRef.current = setTimeout(() => {
        setPhase("sending_to_gw");
        addLog(t.log1, "info");
        addLog(t.log2, "info");
        
        // Travel to GW (slower: 2200ms travel time)
        timerRef.current = setTimeout(() => {
          setPhase("scanning");
          addLog(t.log3, "info");
          addLog(t.log4, "success");
          
          // Scan at GW (slower: 3200ms scan time)
          timerRef.current = setTimeout(() => {
            setPhase("sending_to_tpa");
            addLog(t.log5, "info");
            
            // Travel to TPA (slower: 2200ms travel time)
            timerRef.current = setTimeout(() => {
              setPhase("adjudicating");
              addLog(t.log6, "info");
              addLog(t.log7, "warn");
              
              // Adjudicate at Payer (slower: 3000ms adjudication time)
              timerRef.current = setTimeout(() => {
                setPhase("returning");
                addLog(t.log8, "success");
                addLog(t.log9, "info");
                
                // Return travel to Hospital (slower: 4500ms return time)
                timerRef.current = setTimeout(() => {
                  setPhase("approved");
                  addLog(t.log10, "success");
                  addLog(t.log11, "success");
                  
                  // Keep success state visible for 5 seconds before reset
                  timerRef.current = setTimeout(runCycle, 5000);
                }, 4500);
              }, 3000);
            }, 2200);
          }, 3200);
        }, 2200);
      }, 1500);
    };

    runCycle();
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, [mounted]);

  // Fallback static shell layout during server-side pre-rendering to prevent any hydration mismatch
  if (!mounted) {
    return (
      <div style={{
        width: "100%",
        maxWidth: 680,
        height: 418,
        backgroundImage: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 24,
        boxShadow: "0 32px 64px -16px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 1)"
      }} />
    );
  }

  return (
    <div
      className="nhcx-hero-card"
      style={{
        width: "100%", maxWidth: 680,
        backgroundImage: "linear-gradient(180deg, #FFFFFF 0%, #F8FAFC 100%)",
        border: "1px solid rgba(15, 23, 42, 0.08)",
        borderRadius: 24, padding: "28px",
        boxShadow: "0 32px 64px -16px rgba(15, 23, 42, 0.08), inset 0 1px 0 rgba(255, 255, 255, 1)",
        display: "flex", flexDirection: "column", gap: 24, position: "relative", overflow: "hidden"
      }}
    >
      <style>{`
        @media (max-width: 680px) {
          .nhcx-hero-card {
            padding: 16px 12px !important;
            border-radius: 18px !important;
            gap: 14px !important;
          }
        }
      `}</style>
      
      {/* Background Micro Details */}
      <div style={{
        position: "absolute", top: 0, left: 0, right: 0, bottom: 0,
        backgroundImage: "radial-gradient(rgba(15, 23, 42, 0.04) 1px, transparent 1px)",
        backgroundSize: "16px 16px", opacity: 0.8, pointerEvents: "none"
      }} />

      {/* Top Header */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", position: "relative", zIndex: 10 }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <div style={{ position: "relative", width: 12, height: 12, display: "flex", alignItems: "center", justifyContent: "center" }}>
            <motion.div animate={{ scale: [1, 2.0, 1], opacity: [0.6, 0, 0.6] }} transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }} style={{ position: "absolute", width: "100%", height: "100%", borderRadius: "50%", backgroundColor: "#10B981" }} />
            <div style={{ width: 8, height: 8, borderRadius: "50%", backgroundColor: "#10B981", boxShadow: "0 0 10px rgba(16, 185, 129, 0.8)" }} />
          </div>
          <span style={{ fontSize: 13, fontWeight: 700, color: "#0F172A", letterSpacing: "0.02em" }}>
            {t.liveRouter}
          </span>
        </div>
        <div style={{ display: "flex", alignItems: "center", gap: 6, backgroundColor: "#F1F5F9", padding: "6px 12px", borderRadius: 8, border: "1px solid #E2E8F0", boxShadow: "inset 0 1px 2px rgba(255,255,255,0.5)" }}>
          <Activity size={14} color="#64748B" />
          <span style={{ fontSize: 10, fontWeight: 700, color: "#475569" }}>
            NHCX
          </span>
        </div>
      </div>

      {/* Main Routing Visualizer */}
      <div style={{ position: "relative", height: 160, display: "flex", alignItems: "center", justifyContent: "center", width: "100%", marginTop: 10 }}>
        
        {/* Single Track Line - Perfectly symmetric */}
        <div style={{ position: "absolute", left: "15%", right: "15%", height: 3, top: "50%", marginTop: -1.5, backgroundColor: "#E2E8F0", borderRadius: 2, zIndex: 1 }}>
          {/* Animated Glow Line */}
          <motion.div
            animate={{ backgroundPosition: (phase === "returning" || phase === "approved") ? ["-200% 0", "200% 0"] : ["200% 0", "-200% 0"] }}
            transition={{ duration: phase === "idle" ? 6.0 : 4.0, repeat: Infinity, ease: "linear" }}
            style={{
              width: "100%", height: "100%", borderRadius: 2,
              backgroundImage: phase === "idle"
                ? "linear-gradient(90deg, transparent 0%, rgba(100, 116, 139, 0.15) 50%, transparent 100%)"
                : (phase === "returning" || phase === "approved")
                ? "linear-gradient(90deg, transparent 0%, rgba(16, 185, 129, 0.6) 50%, transparent 100%)"
                : "linear-gradient(90deg, transparent 0%, rgba(139, 92, 246, 0.6) 50%, transparent 100%)",
              backgroundSize: "200% 100%"
            }}
          />
        </div>

        {/* Curved Dashed Trajectory Arc (Payer -> Chikitsa EMR) */}
        <svg
          style={{ position: "absolute", top: 0, left: 0, width: "100%", height: "100%", zIndex: 1, pointerEvents: "none" }}
          viewBox="0 0 600 160"
          preserveAspectRatio="none"
        >
          <motion.path
            d="M 90,80 Q 300,-40 510,80"
            fill="none"
            stroke={phase === "returning" ? "#10B981" : "#E2E8F0"}
            strokeWidth={1.5}
            strokeDasharray="5,5"
            animate={phase === "returning" ? { strokeDashoffset: [0, 10] } : { strokeDashoffset: 0 }}
            transition={{ repeat: Infinity, duration: 1.0, ease: "linear" }}
            style={{
              opacity: phase === "returning" ? 1 : 0.25,
              filter: phase === "returning" ? "drop-shadow(0 0 4px rgba(16, 185, 129, 0.4))" : "none"
            }}
          />
        </svg>

        {/* --- NODES --- */}
        {/* 1. Hospital Node */}
        <div style={{
          position: "absolute",
          left: "15%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10,
          width: 60,
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          {/* Pulsing LED Indicator or Success Tick */}
          {phase === "approved" ? (
            <div style={{
              position: "absolute",
              top: -5,
              right: -5,
              width: 14,
              height: 14,
              borderRadius: "50%",
              backgroundColor: "#10B981",
              border: "1.5px solid #fff",
              boxShadow: "0 0 10px rgba(16, 185, 129, 0.8)",
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              zIndex: 15
            }}>
              <svg viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth={4} style={{ width: 8, height: 8 }}>
                <polyline points="20 6 9 17 4 12" />
              </svg>
            </div>
          ) : (
            <div style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, zIndex: 15 }}>
              <motion.div
                animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  top: 0, left: 0, width: "100%", height: "100%",
                  borderRadius: "50%",
                  backgroundColor: (phase === "idle") ? "#10B981" : "#FBBF24"
                }}
              />
              <div style={{
                position: "absolute",
                top: 0, left: 0, width: "100%", height: "100%",
                borderRadius: "50%",
                backgroundColor: (phase === "idle") ? "#10B981" : "#FBBF24",
                border: "2px solid #fff",
                boxShadow: (phase === "idle") ? "0 0 10px rgba(16, 185, 129, 0.8)" : "0 0 10px rgba(251, 191, 36, 0.8)"
              }} />
            </div>
          )}

          {/* Icon Container */}
          <motion.div
            animate={phase === "sending_to_gw" ? { y: [0, -4, 0] } : {}}
            transition={{ repeat: Infinity, duration: 2.0, ease: "easeInOut" }}
            style={{
              width: "100%", height: "100%", borderRadius: 16, backgroundColor: "#fff",
              border: "1px solid rgba(139, 92, 246, 0.2)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2,
              boxShadow: "0 8px 24px -6px rgba(139, 92, 246, 0.15), inset 0 2px 4px rgba(255,255,255,1)"
            }}
          >
            <MonitorUp size={28} color="#7C3AED" strokeWidth={1.5} />
          </motion.div>

          {/* Labels Below (Taken out of vertical flow to maintain perfect centering) */}
          <div style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "max-content",
            pointerEvents: "none"
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>{t.hospital}</div>
          </div>
        </div>

        {/* 2. NHCX Gateway Node */}
        <div style={{
          position: "absolute",
          left: "50%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10,
          width: 68,
          height: 68,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          {/* Radar Scanner Animation (Smooth and slow pulse ripples) */}
          <AnimatePresence>
            {phase === "scanning" && (
              <>
                {/* Slow Pulse Ring 1 */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                  transition={{ duration: 3.6, repeat: Infinity, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    position: "absolute",
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    border: "2px solid #0EA5E9",
                    pointerEvents: "none",
                    left: "50%",
                    top: "50%",
                    marginLeft: -34,
                    marginTop: -34
                  }}
                />
                {/* Slow Pulse Ring 2 */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                  transition={{ duration: 3.6, delay: 1.2, repeat: Infinity, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    position: "absolute",
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    border: "2px solid #0EA5E9",
                    pointerEvents: "none",
                    left: "50%",
                    top: "50%",
                    marginLeft: -34,
                    marginTop: -34
                  }}
                />
                {/* Slow Pulse Ring 3 */}
                <motion.div
                  initial={{ scale: 0.5, opacity: 0 }}
                  animate={{ scale: [1, 2.2], opacity: [0.7, 0] }}
                  transition={{ duration: 3.6, delay: 2.4, repeat: Infinity, ease: [0.16, 1, 0.3, 1] }}
                  style={{
                    position: "absolute",
                    width: 68,
                    height: 68,
                    borderRadius: "50%",
                    border: "2px solid #0EA5E9",
                    pointerEvents: "none",
                    left: "50%",
                    top: "50%",
                    marginLeft: -34,
                    marginTop: -34
                  }}
                />
              </>
            )}
          </AnimatePresence>

          {/* Pulsing LED Indicator */}
          <div style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, zIndex: 15 }}>
            <motion.div
              animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
              transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
              style={{
                position: "absolute",
                top: 0, left: 0, width: "100%", height: "100%",
                borderRadius: "50%",
                backgroundColor: phase === "scanning" ? "#10B981" : (phase === "idle" || phase === "approved") ? "#0EA5E9" : "#FBBF24"
              }}
            />
            <div style={{
              position: "absolute",
              top: 0, left: 0, width: "100%", height: "100%",
              borderRadius: "50%",
              backgroundColor: phase === "scanning" ? "#10B981" : (phase === "idle" || phase === "approved") ? "#0EA5E9" : "#FBBF24",
              border: "2px solid #fff",
              boxShadow: phase === "scanning" ? "0 0 10px rgba(16, 185, 129, 0.8)" : (phase === "idle" || phase === "approved") ? "0 0 10px rgba(14, 165, 233, 0.8)" : "0 0 10px rgba(251, 191, 36, 0.8)"
            }} />
          </div>

          {/* Icon Container */}
          <div style={{
            width: "100%", height: "100%", borderRadius: 20, backgroundColor: "#fff",
            border: "2px solid #0EA5E9",
            display: "flex", alignItems: "center", justifySelf: "stretch", justifyContent: "center", zIndex: 2,
            boxShadow: phase === "scanning" ? "0 0 24px rgba(14, 165, 233, 0.3), inset 0 2px 4px rgba(255,255,255,1)" : "0 8px 24px -6px rgba(14, 165, 233, 0.15), inset 0 2px 4px rgba(255,255,255,1)"
          }}>
            <Workflow size={32} color="#0284C7" strokeWidth={1.5} />
          </div>

          {/* Labels Below (Taken out of vertical flow to maintain perfect centering) */}
          <div style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "max-content",
            pointerEvents: "none"
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>{t.gateway}</div>
            <div style={{ fontSize: 9, color: "#64748B", marginTop: 2, display: "flex", alignItems: "center", gap: 3, backgroundColor: "rgba(14, 165, 233, 0.1)", padding: "2px 6px", borderRadius: 4 }}>
              <Sparkles size={10} color="#0284C7" /> ClaimIQ AI
            </div>
          </div>
        </div>

        {/* 3. TPA Node */}
        <div style={{
          position: "absolute",
          left: "85%",
          top: "50%",
          transform: "translate(-50%, -50%)",
          zIndex: 10,
          width: 60,
          height: 60,
          display: "flex",
          alignItems: "center",
          justifyContent: "center"
        }}>
          {/* Pulsing LED Indicator */}
          <div style={{ position: "absolute", top: -4, right: -4, width: 10, height: 10, zIndex: 15 }}>
            {phase !== "idle" && phase !== "approved" && phase !== "sending_to_gw" && phase !== "scanning" && (
              <motion.div
                animate={{ scale: [1, 2.2, 1], opacity: [0.6, 0, 0.6] }}
                transition={{ repeat: Infinity, duration: 1.6, ease: "easeInOut" }}
                style={{
                  position: "absolute",
                  top: 0, left: 0, width: "100%", height: "100%",
                  borderRadius: "50%",
                  backgroundColor: phase === "adjudicating" ? "#10B981" : "#FBBF24"
                }}
              />
            )}
            <div style={{
              position: "absolute",
              top: 0, left: 0, width: "100%", height: "100%",
              borderRadius: "50%",
              backgroundColor: phase === "adjudicating" ? "#10B981" : (phase === "sending_to_tpa" || phase === "returning") ? "#FBBF24" : "#64748B",
              border: "2px solid #fff",
              boxShadow: phase === "adjudicating" ? "0 0 10px rgba(16, 185, 129, 0.8)" : (phase === "sending_to_tpa" || phase === "returning") ? "0 0 10px rgba(251, 191, 36, 0.8)" : "none"
            }} />
          </div>

          {/* Icon Container */}
          <motion.div
            animate={phase === "adjudicating" ? { scale: [1, 1.05, 1], borderColor: ["rgba(16, 185, 129, 0.2)", "#10B981", "rgba(16, 185, 129, 0.2)"] } : {}}
            transition={{ repeat: Infinity, duration: 2.0, ease: "easeInOut" }}
            style={{
              width: "100%", height: "100%", borderRadius: 16, backgroundColor: "#fff",
              border: "1px solid rgba(16, 185, 129, 0.2)",
              display: "flex", alignItems: "center", justifyContent: "center", zIndex: 2,
              boxShadow: "0 8px 24px -6px rgba(16, 185, 129, 0.15), inset 0 2px 4px rgba(255,255,255,1)"
            }}
          >
            <Landmark size={28} color="#059669" strokeWidth={1.5} />
          </motion.div>

          {/* Labels Below (Taken out of vertical flow to maintain perfect centering) */}
          <div style={{
            position: "absolute",
            top: "100%",
            left: "50%",
            transform: "translateX(-50%)",
            marginTop: 12,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            width: "max-content",
            pointerEvents: "none"
          }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: "#0F172A" }}>{t.payer}</div>
          </div>
        </div>

        {/* --- PAYLOADS --- */}
        {/* Outbound Payload - Moves perfectly on center line */}
        <AnimatePresence>
          {(phase === "sending_to_gw" || phase === "scanning" || phase === "sending_to_tpa") && (
            <motion.div
              initial={{ left: "15%", x: "-50%", y: "-50%", opacity: 0, scale: 0.8 }}
              animate={{
                left: phase === "sending_to_gw" ? "50%" : phase === "scanning" ? "50%" : "85%",
                opacity: 1,
                scale: 1
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: phase === "sending_to_gw" ? 2.2 : phase === "sending_to_tpa" ? 2.2 : 0, ease: "easeInOut" }}
              style={{
                position: "absolute",
                top: "50%",
                zIndex: 20,
                backgroundColor: "rgba(255, 255, 255, 0.95)",
                backdropFilter: "blur(8px)",
                border: "1px solid #7C3AED",
                borderRadius: 999,
                padding: "5px 12px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 8px 20px -4px rgba(139, 92, 246, 0.3), 0 0 12px rgba(139, 92, 246, 0.1)",
                whiteSpace: "nowrap"
              }}
            >
              <FileJson size={12} color="#7C3AED" />
              <span style={{ fontSize: 9, fontWeight: 800, color: "#7C3AED", letterSpacing: "0.05em" }}>
                {(phase === "sending_to_gw" || phase === "scanning") ? t.bundleSent : t.claimRouted}
              </span>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Return Payload - Moves on curved dashed trajectory path directly */}
        <AnimatePresence>
          {phase === "returning" && (
            <motion.div
              initial={{ left: "85%", x: "-50%", y: 0, opacity: 0, scale: 0.8 }}
              animate={{
                left: "15%",
                y: [0, -60, 0],
                opacity: 1,
                scale: 1
              }}
              exit={{ opacity: 0, scale: 0.5 }}
              transition={{ duration: 4.5, ease: "easeInOut" }}
              style={{
                position: "absolute",
                top: "50%",
                marginTop: -12,
                zIndex: 20,
                backgroundColor: "rgba(16, 185, 129, 0.95)",
                backdropFilter: "blur(8px)",
                border: "1px solid #059669",
                borderRadius: 999,
                padding: "5px 12px",
                display: "flex",
                alignItems: "center",
                gap: 6,
                boxShadow: "0 8px 20px -4px rgba(16, 185, 129, 0.4), 0 0 12px rgba(16, 185, 129, 0.15)",
                whiteSpace: "nowrap"
              }}
            >
              <CheckCircle2 size={12} color="#fff" strokeWidth={2.5} />
              <span style={{ fontSize: 9, fontWeight: 800, color: "#fff", letterSpacing: "0.05em" }}>{t.approvedAmt}</span>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Premium Terminal Log */}
      <div style={{
        backgroundColor: "#1E293B", borderRadius: 16, padding: "16px 20px",
        height: 140, display: "flex", flexDirection: "column",
        border: "1px solid rgba(148, 163, 184, 0.12)",
        boxShadow: "0 24px 48px -12px rgba(0, 0, 0, 0.15), inset 0 1px 0 rgba(255, 255, 255, 0.05)",
        position: "relative", zIndex: 10
      }}>
        {/* Terminal Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10, borderBottom: "1px solid rgba(255,255,255,0.08)", paddingBottom: 10 }}>
          <div style={{ display: "flex", gap: 6 }}>
            <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#EF4444" }} />
            <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#F59E0B" }} />
            <div style={{ width: 10, height: 10, borderRadius: "50%", backgroundColor: "#10B981" }} />
          </div>
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <div style={{ width: 6, height: 6, borderRadius: "50%", backgroundColor: phase !== "idle" ? "#10B981" : "#64748B", boxShadow: phase !== "idle" ? "0 0 6px #10B981" : "none" }} />
            <span style={{ fontSize: 10, fontFamily: "var(--font-mono)", color: "#94A3B8", letterSpacing: "0.05em" }}>nhcx-router-tty1</span>
          </div>
        </div>

        {/* Log Entries */}
        <div style={{ display: "flex", flexDirection: "column", gap: 6, flex: 1, justifyContent: "flex-end", overflow: "hidden" }}>
          {logs.map((log, i) => (
            <motion.div
              key={`${log.msg}-${i}`}
              initial={{ opacity: 0, x: -10 }}
              animate={{ opacity: 1, x: 0 }}
              style={{
                fontFamily: "var(--font-mono)", fontSize: 11, display: "flex", alignItems: "center", gap: 8,
                color: log.type === "success" ? "#34D399" : log.type === "warn" ? "#FBBF24" : "#E2E8F0",
                fontWeight: log.type === "info" ? 400 : 600
              }}
            >
              <span style={{ color: "#94A3B8", fontSize: 9 }}>[{log.time}]</span>
              <ChevronRight size={12} color="#94A3B8" style={{ flexShrink: 0 }} />
              <span style={{ textShadow: log.type === "success" ? "0 0 12px rgba(52, 211, 153, 0.4)" : "none" }}>{log.msg}</span>
            </motion.div>
          ))}
          {logs.length === 0 && (
            <div style={{ fontFamily: "var(--font-mono)", fontSize: 11, color: "#94A3B8", display: "flex", alignItems: "center", gap: 8 }}>
              <ChevronRight size={12} color="#94A3B8" /> {t.waitingPayload}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
